import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, Check, ArrowLeft, Landmark, Copy, Upload, Loader2, Clock, XCircle, CheckCircle2, Plus, Download, Tag, Palmtree } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { usePaymentStatus } from "@/components/PaymentReminder";
import api, { formatError } from "@/lib/api";
import useLiveGrades from "@/lib/useLiveGrades";
import PlanCalendar from "@/components/PlanCalendar";
import { toast } from "sonner";
import { UpgradeTip } from "@/components/ReadingUpsell";
import TrialEndedNote from "@/components/trial/TrialEndedNote";

const PLAN_CARDS = [
  { id: "one", title: "1 subject", price: 200, note: "Pick any one subject" },
  { id: "three", title: "3 subjects", price: 500, note: "Pick any three subjects", best: false, reading: true },
  { id: "all", title: "All subjects", price: 800, note: "Every subject of your child's grade", best: true, reading: true },
];
const READING_PLANS = ["three", "all", "holiday"];

const fmtDay = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

// The Holiday Pack and the Reading kit, big and clear, with their conditions
function Featured({ id, color, icon, title, price, unit, points, badge }) {
  return (
    <div className={`nb-card p-6 ${color}`} data-testid={`featured-${id}`}>
      <p className="font-kids text-2xl font-bold flex items-center gap-2">{icon} {title}</p>
      <p className="mt-2"><span className="text-4xl font-bold" data-testid={`featured-${id}-price`}>{price}</span> <span className="text-slate-600 text-sm">{unit}</span></p>
      {badge && <p className="mt-3 inline-block rounded-full bg-green-100 border-2 border-green-500 px-3 py-1 text-xs font-bold text-green-800" data-testid={`featured-${id}-reading`}>{badge}</p>}
      <ul className="mt-4 space-y-2 text-sm">
        {points.map((x, k) => <li key={k} className="flex gap-2"><Check size={16} className="shrink-0 mt-0.5 text-green-700" />{x}</li>)}
      </ul>
    </div>
  );
}

function FeaturedPlans({ price, spots }) {
  return (
    <div className="grid sm:grid-cols-2 gap-5 mt-8" data-testid="pricing-featured">
      <Featured id="holiday" color="bg-orange-50" icon={<Palmtree />} title="Holiday Pack" price={`Rs ${price}`} unit="per child · one payment" badge="+ Reading kit included (EN & FR)"
        points={["15 November to 15 January (school holidays)", "All subjects of the child's grade, mixed revision", "Reading kit with Kiko included (English & French)",
          `Limited: only the first ${spots} children get a spot`]} />
      <Featured id="reading" color="bg-sky-50" icon={<span aria-hidden>📖</span>} title="Reading kit with Kiko" price="Rs 200" unit="per child · per language · per month"
        points={["FREE with 3 subjects (Rs 500), All subjects (Rs 800) and the Holiday Pack", "Can be bought on its own: no subject plan needed", "English and French are separate: choose one or both",
          "Phonics levels 1 to 3, videos, flash cards, 100+ texts, certificates", "For every child, whatever the grade (Petit lecteur / Grand lecteur)"]} />
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hi, setHi] = useState(null);
  useEffect(() => { api.get("/holiday/info").then(({ data }) => setHi(data)).catch(() => {}); }, []);
  const spots = hi?.spots ?? 500;
  return (
    <div className="min-h-screen bg-[#EAF7F4] font-parent text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#EAF7F4]/70 border-b border-slate-900/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-blue-600 border-2 border-slate-900 flex items-center justify-center shadow-[2px_2px_0_0_#0F172A]">
              <Rocket color="#fff" size={18} />
            </div>
            <span className="font-kids text-lg font-bold">Anou Appran</span>
          </Link>
          <Button variant="ghost" className="rounded-full" onClick={() => navigate(user ? "/parent" : "/")} data-testid="pricing-back">
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-5 py-14">
        <TrialEndedNote />
        <div className="text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase bg-yellow-300 border-2 border-slate-900 rounded-full px-3 py-1 shadow-[3px_3px_0_0_#0F172A]">
            <Palmtree size={14} /> Holiday Pack · only {spots} spots
          </span>
          <h1 className="font-kids text-4xl sm:text-5xl font-bold mt-5">Choose your plan</h1>
          <p className="mt-3 text-slate-600" data-testid="pricing-validity">Monthly plans per child, all year round. Holiday Pack (15 November to 15 January) can be booked now — only the first {spots} children get a spot. Grades 1 to 6 · full Mauritian syllabus.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {PLAN_CARDS.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
              className={`nb-card p-6 text-center relative ${p.best ? "bg-yellow-50" : "bg-white"}`} data-testid={`plan-card-${p.id}`}>
              {p.best && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[11px] font-bold rounded-full px-3 py-1 border-2 border-slate-900">Best value</span>}
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">{p.title}</p>
              <p className="text-4xl font-bold mt-3" data-testid={`plan-price-${p.id}`}>Rs {p.price}</p>
              <p className="text-slate-500 text-sm mt-1">per child / month</p>
              <p className="text-sm text-slate-600 mt-4">{p.note}</p>
              {p.reading && <p className="mt-3 inline-block rounded-full bg-green-100 border-2 border-green-500 px-3 py-1 text-xs font-bold text-green-800" data-testid={`plan-reading-${p.id}`}>+ Reading kit included (EN & FR)</p>}
              {p.id === "one" && <p className="mt-3 text-xs text-slate-600" data-testid="plan-one-upsell">Reading kit: Rs 200/language. <b className="text-green-700">Free with 3 subjects (Rs 500).</b><span className="block">Kit Lecture offert avec 3 matières.</span></p>}
            </motion.div>
          ))}
        </div>
        <FeaturedPlans price={hi?.price ?? 500} spots={spots} />
        <div className="mt-10"><PlanCalendar holidayPrice={hi?.price ?? 500} spots={spots} /></div>
        <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
          <Feat>All chapters &amp; all 3 levels of each chosen subject</Feat>
          <Feat>Read-aloud, step-by-step Maths copybook</Feat>
          <Feat>English &amp; French versions for Maths, Science, History &amp; Geography</Feat>
          <Feat>Parent progress tracking</Feat>
        </ul>
        {user ? <BillingForm /> : (
          <div className="nb-card bg-white p-6 mt-10 text-center">
            <p className="font-semibold">Create a free account to try the demo and choose your plan.</p>
            <Button onClick={() => navigate("/register")} className="mt-4 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="pricing-register">Create my account</Button>
          </div>
        )}
        <PaymentDetails loggedIn={Boolean(user)} />
      </main>
    </div>
  );
}

function BillingForm() {
  const [info, setInfo] = useState(null);
  const [sel, setSel] = useState({});
  const [rsel, setRsel] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState(null);
  const [mode, setMode] = useState("monthly");
  const [hol, setHol] = useState(null);
  const load = () => api.get("/billing").then(({ data }) => setInfo(data)).catch(() => {});
  const pre = new URLSearchParams(window.location.search).get("holiday");
  useEffect(() => {
    load();
    api.get("/holiday/info").then(({ data }) => {
      setHol(data);
      if (pre && !data.full) setMode("holiday");
      if (pre && !data.full) {
        setSel({ [pre]: { plan: "holiday" } });
        setTimeout(() => document.querySelector('[data-testid="billing-form"]')?.scrollIntoView({ behavior: "smooth" }), 400);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!info) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-blue-600" /></div>;

  const listPrice = (cid) => (sel[cid]?.plan === "holiday" ? hol.price : info.plans[sel[cid]?.plan]?.price || 0);
  const switchMode = (m) => { setMode(m); setSel({}); setRsel({}); };
  const price = (cid) => {
    const p = listPrice(cid);
    if (!promo || !p) return p;
    return Math.max(0, p - (promo.kind === "percent" ? Math.round((p * promo.value) / 100) : promo.value));
  };
  const readingFree = (c) => c.reading_included || READING_PLANS.includes(sel[c.id]?.plan);
  const rlen = (c) => (mode === "holiday" || readingFree(c) ? 0 : rsel[c.id]?.length || 0);
  const listTotal = info.children.reduce((t, c) => t + listPrice(c.id) + READING_PRICE * rlen(c), 0);
  const disc = (p) => (!promo || !p ? p : Math.max(0, p - (promo.kind === "percent" ? Math.round((p * promo.value) / 100) : promo.value)));
  const total = info.children.reduce((t, c) => t + price(c.id) + (rlen(c) ? disc(READING_PRICE * rlen(c)) : 0), 0);
  const items = [...info.children.filter((c) => sel[c.id]?.plan).map((c) => ({ child_id: c.id, plan: sel[c.id].plan, subjects: sel[c.id].subjects || [] })),
    ...info.children.filter((c) => rlen(c)).map((c) => ({ child_id: c.id, plan: "reading", langs: rsel[c.id] }))];

  const submit = async () => {
    if (!items.length) return toast.error("Please choose a plan for at least one child.");
    if (!file) return toast.error("Please add the screenshot of your bank transfer.");
    const fd = new FormData();
    fd.append("items", JSON.stringify(items));
    fd.append("file", file);
    if (promo) fd.append("promo_code", promo.code);
    setBusy(true);
    try {
      await api.post("/billing/submit", fd);
      toast.success("Thank you! We received your payment screenshot and will confirm it shortly.");
      setSel({}); setRsel({}); setFile(null); setPromo(null); load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="nb-card bg-white p-6 sm:p-8 mt-10" data-testid="billing-form">
      {hol?.on_sale && (
        <p className="mb-4 rounded-2xl bg-orange-100 border-2 border-orange-400 px-4 py-3 font-semibold text-sm flex items-center gap-2" data-testid="billing-holiday-season">
          <Palmtree size={16} /> Holiday season (15 Nov – 15 Jan): choose the Holiday Pack (Rs {hol.price}, all subjects mixed) or keep the full monthly plan.
        </p>
      )}
      {hol && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="billing-mode-tabs">
          <button onClick={() => switchMode("monthly")} data-testid="billing-mode-monthly"
            className={`rounded-full border-2 border-slate-900 px-5 py-2 font-semibold text-sm transition-colors ${mode === "monthly" ? "bg-blue-600 text-white" : "bg-white hover:bg-slate-50"}`}>Monthly plan · full syllabus</button>
          <button onClick={() => !hol.full && switchMode("holiday")} disabled={hol.full} data-testid="billing-mode-holiday"
            className={`rounded-full border-2 border-slate-900 px-5 py-2 font-semibold text-sm inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 ${mode === "holiday" ? "bg-orange-500 text-white" : "bg-white hover:bg-orange-50"}`}>
            <Palmtree size={14} /> {hol.full ? "Holiday Pack · fully booked" : "Switch to the Holiday Pack"}
          </button>
        </div>
      )}
      {hol?.full && <Waitlist kids={info.children.filter((c) => !info.holiday_booked?.includes(c.id))} />}
      <h2 className="font-kids text-2xl font-bold">1. {mode === "holiday" ? "Choose the children for the Holiday Revision Pack" : "Choose a plan for each child"}</h2>
      {mode === "holiday" && (
        <p className="text-slate-600 text-sm mt-1" data-testid="billing-holiday-info">
          One pack per child mixing all the subjects of their grade (one question of each subject in turn), plus questions from next year's first chapter with detailed explanations. Access from {fmtDay(hol.sale_start)} to {fmtDay(hol.access_end)}, whatever the day you pay. <b className="text-red-600">Only {hol.spots} spots — book yours now!</b> Counts as 2 months towards the 12-months-in-a-row gift.
        </p>
      )}
      {info.children.length === 0 ? <AddChildInline onAdded={load} /> : (
        <div className="mt-5 space-y-4">
          {mode === "holiday" ? info.children.map((c) => {
            const booked = info.holiday_booked?.includes(c.id);
            return (
              <label key={c.id} className={`flex items-center gap-3 rounded-2xl border-2 p-4 ${booked ? "opacity-70 border-green-300 bg-green-50" : "cursor-pointer"} ${sel[c.id] ? "border-orange-400 bg-orange-50" : booked ? "" : "border-slate-200"}`} data-testid={`billing-holiday-child-${c.id}`}>
                <input type="checkbox" disabled={booked} checked={Boolean(sel[c.id])} onChange={(e) => setSel({ ...sel, [c.id]: e.target.checked ? { plan: "holiday" } : undefined })} className="w-5 h-5" />
                <span className="font-semibold flex-1">{c.name} · Grade {c.grade}</span>
                <span className="text-sm text-slate-500">{c.subjects.join(", ")}</span>
                {booked ? <span className="font-bold text-green-700 text-sm" data-testid={`billing-holiday-booked-${c.id}`}>Spot booked</span> : <span className="font-bold">Rs {hol.price}</span>}
              </label>
            );
          }) : info.children.map((c) => (
            <div key={c.id} className="space-y-2">
              <ChildPlan child={c} subjects={info.subjects} plans={info.plans} value={sel[c.id] || {}} onChange={(v) => setSel({ ...sel, [c.id]: v })} />
              <ReadingPick child={c} included={readingFree(c)} value={rsel[c.id] || []} onChange={(v) => setRsel({ ...rsel, [c.id]: v })} />
            </div>
          ))}
        </div>
      )}
      <PromoField promo={promo} setPromo={setPromo} />
      <div className="mt-4 flex items-center justify-between rounded-2xl bg-slate-900 text-white px-5 py-4">
        <span className="font-semibold">{mode === "holiday" ? "Total for the holiday" : "Total to pay this month"}</span>
        <span className="text-right">
          {promo && listTotal > total && <span className="text-sm text-slate-400 line-through mr-2" data-testid="billing-list-total">Rs {listTotal}</span>}
          <span className="text-2xl font-bold" data-testid="billing-total">Rs {total}</span>
        </span>
      </div>
      <h2 className="font-kids text-2xl font-bold mt-8">2. Pay by bank transfer</h2>
      <p className="text-slate-600 text-sm mt-1">Use the MCB account details below, then come back here.</p>
      <h2 className="font-kids text-2xl font-bold mt-8">3. Send the payment screenshot</h2>
      <label className="mt-3 flex items-center gap-3 rounded-2xl border-2 border-dashed border-slate-400 p-4 cursor-pointer hover:bg-slate-50" data-testid="billing-file-label">
        <Upload size={20} className="text-blue-600" />
        <span className="text-sm flex-1">{file ? file.name : "Tap to add a screenshot or PDF (max 10 MB)"}</span>
        <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" data-testid="billing-file"
          onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      <Button onClick={submit} disabled={busy} className="mt-5 w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 border-2 border-slate-900 font-semibold" data-testid="billing-submit">
        {busy ? <Loader2 className="animate-spin" size={18} /> : "Send my payment"}
      </Button>
      <Submissions list={info.submissions} />
    </motion.section>
  );
}

function PromoField({ promo, setPromo }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/promo/check", { code });
      setPromo(data);
      toast.success(`Promo code ${data.code} applied (${data.label})`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  if (promo) return (
    <div className="mt-6 flex items-center gap-2 rounded-2xl bg-green-50 border-2 border-green-500 px-4 py-3 text-sm" data-testid="billing-promo-applied">
      <Tag size={16} className="text-green-700" />
      <span className="flex-1 font-semibold text-green-800">Code {promo.code} applied · {promo.label}</span>
      <button onClick={() => setPromo(null)} className="text-xs font-semibold text-slate-600 hover:underline" data-testid="billing-promo-remove">Remove</button>
    </div>
  );
  return (
    <div className="mt-6 flex gap-2" data-testid="billing-promo">
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && apply()}
        placeholder="Promo code (optional)" className="flex-1 h-11 rounded-xl border-2 border-slate-300 px-3 uppercase focus:border-slate-900 outline-none" data-testid="billing-promo-input" />
      <Button onClick={apply} disabled={busy || !code.trim()} variant="outline" className="h-11 rounded-full border-2 border-slate-900" data-testid="billing-promo-apply">
        {busy ? <Loader2 className="animate-spin" size={16} /> : "Apply"}
      </Button>
    </div>
  );
}

const READING_PRICE = 200;

// Reading / Lecture: included with 3 subjects, all subjects and the Holiday Pack; otherwise Rs 200 per child and per language.
function ReadingPick({ child, included, value, onChange }) {
  const toggle = (l) => onChange(value.includes(l) ? value.filter((x) => x !== l) : [...value, l]);
  const until = child.reading_until || {};
  if (included) return (
    <div className="rounded-2xl border-2 border-green-500 bg-green-50 p-4 flex items-center gap-2" data-testid={`billing-reading-included-${child.id}`}>
      <Check size={16} className="text-green-700" />
      <p className="font-bold text-sm text-green-800">📖 Reading with Kiko / Lecture avec Kiko (English & French) · included{child.reading_included ? " with the current plan" : " with this plan"}</p>
    </div>
  );
  return (
    <div className="rounded-2xl border-2 border-green-300 bg-green-50 p-4" data-testid={`billing-reading-${child.id}`}>
      <p className="font-bold text-sm">📖 Reading with Kiko / Lecture avec Kiko <span className="font-normal text-slate-500">· Rs {READING_PRICE}/month per language</span></p>
      <p className="text-xs text-slate-600 mt-1" data-testid={`billing-reading-alone-${child.id}`}>No subject plan needed: the Reading kit can be bought on its own. / Pas besoin de matières : le Kit Lecture s'achète seul.</p>
      <div className="flex flex-wrap gap-2 mt-2">
        {[["en", "English Reading"], ["fr", "Lecture en français"]].map(([l, label]) => (
          <button key={l} onClick={() => toggle(l)} data-testid={`billing-reading-${child.id}-${l}`}
            className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition-colors ${value.includes(l) ? "bg-green-600 text-white border-slate-900" : "bg-white border-slate-300 hover:border-slate-900"}`}>
            {value.includes(l) && <Check size={12} className="inline mr-1" />}{label}
            {until[l] && new Date(until[l]) > new Date() && <span className="ml-1 text-xs opacity-80">(active)</span>}
          </button>
        ))}
      </div>
      <UpgradeTip testId={`billing-reading-upsell-${child.id}`} />
    </div>
  );
}

function ChildPlan({ child, subjects: allSubjects, plans, value, onChange }) {
  const subjects = child.subjects || allSubjects;
  const need = plans[value.plan]?.subjects || 0;
  const chosen = value.plan === "all" ? subjects : value.subjects || [];
  const toggle = (s) => {
    const cur = value.subjects || [];
    if (cur.includes(s)) return onChange({ ...value, subjects: cur.filter((x) => x !== s) });
    if (cur.length >= need) return toast.error(`This plan includes ${need} subject${need > 1 ? "s" : ""}.`);
    onChange({ ...value, subjects: [...cur, s] });
  };
  return (
    <div className="rounded-2xl border-2 border-slate-200 p-4" data-testid={`billing-child-${child.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-bold">{child.name} <span className="text-slate-500 font-normal">· Grade {child.grade}</span></p>
        {child.paid_subjects.length > 0 && <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-300 rounded-full px-2 py-0.5" data-testid={`billing-active-${child.id}`}>Active until {new Date(child.paid_until).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}: {child.paid_subjects.join(", ")}</span>}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {Object.entries(plans).map(([id, p]) => (
          <button key={id} onClick={() => onChange(value.plan === id ? {} : { plan: id, subjects: [] })} data-testid={`billing-plan-${child.id}-${id}`}
            className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition-colors ${value.plan === id ? "bg-blue-600 text-white border-slate-900" : "bg-white border-slate-300 hover:border-slate-900"}`}>
            {p.label} · Rs {p.price}/month
          </button>
        ))}
      </div>
      {value.plan && (
        <div className="flex flex-wrap gap-2 mt-3">
          {subjects.map((s) => {
            const on = chosen.includes(s);
            return (
              <button key={s} disabled={value.plan === "all"} onClick={() => toggle(s)} data-testid={`billing-subject-${child.id}-${s}`}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${on ? "bg-green-100 border-green-500 text-green-800" : "bg-slate-50 border-slate-300"}`}>
                {on && <Check size={12} />} {s}
              </button>
            );
          })}
          {value.plan !== "all" && <span className="text-xs text-slate-500 self-center">{chosen.length}/{need} chosen</span>}
        </div>
      )}
    </div>
  );
}

function AddChildInline({ onAdded }) {
  const liveGrades = useLiveGrades();
  const [name, setName] = useState("");
  const [grade, setGrade] = useState("4");
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/children", { name, grade: parseInt(grade, 10), avatar: "fox" }); onAdded(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); }
  };
  return (
    <form onSubmit={add} className="mt-4 flex flex-col sm:flex-row gap-3" data-testid="billing-add-child">
      <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Child's name" className="flex-1 h-11 rounded-xl border-2 border-slate-900 px-3" data-testid="billing-child-name" />
      <select value={grade} onChange={(e) => setGrade(e.target.value)} className="h-11 rounded-xl border-2 border-slate-900 px-3" data-testid="billing-child-grade">
        {liveGrades.map((g) => <option key={g} value={g}>Grade {g}</option>)}
      </select>
      <Button type="submit" className="h-11 rounded-full bg-slate-900" data-testid="billing-child-add"><Plus size={16} className="mr-1" /> Add child</Button>
    </form>
  );
}

const STATUS = {
  pending: { Icon: Clock, cls: "text-amber-700 bg-amber-50 border-amber-300", label: "Waiting for confirmation" },
  approved: { Icon: CheckCircle2, cls: "text-green-700 bg-green-50 border-green-300", label: "Confirmed" },
  rejected: { Icon: XCircle, cls: "text-red-700 bg-red-50 border-red-300", label: "Not accepted" },
};

function Submissions({ list }) {
  if (!list?.length) return null;
  return (
    <div className="mt-8" data-testid="billing-submissions">
      <h3 className="font-bold">Your payments</h3>
      <div className="mt-3 space-y-2">
        {list.map((s) => {
          const st = STATUS[s.status];
          return (
            <div key={s.id} className="rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`billing-sub-${s.id}`}>
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${st.cls}`}><st.Icon size={12} /> {st.label}</span>
              <span className="font-semibold">Rs {s.total}{s.items?.every((i) => i.plan === "holiday") ? " · Holiday Revision Pack" : s.items?.some((i) => i.plan === "holiday") ? " · incl. Holiday Pack" : "/month"}</span>
              {s.promo_code && <span className="text-xs font-semibold text-green-700" data-testid={`billing-sub-promo-${s.id}`}>Code {s.promo_code}</span>}
              <span className="text-slate-500 flex-1">{s.items.map((it) => `${it.child_name}: ${it.subjects.join(", ")}`).join(" · ")}</span>
              {s.invoice_no && <InvoiceButton sub={s} />}
              {s.note && <span className="text-slate-600 w-full">{s.note}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InvoiceButton({ sub }) {
  const download = async () => {
    try {
      const { data } = await api.get(`/billing/invoice/${sub.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `Invoice-${sub.invoice_no}.pdf` });
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the invoice. Please try again.");
    }
  };
  return (
    <button onClick={download} className="inline-flex items-center gap-1 rounded-full border-2 border-slate-900 px-3 py-0.5 text-xs font-semibold hover:bg-slate-100" data-testid={`billing-invoice-${sub.id}`}>
      <Download size={12} /> Invoice {sub.invoice_no}
    </button>
  );
}

const PAY_EMAIL = "marieyanna@kmpmoris.com";
const ACCOUNT = "000456560130";

function MyReference() {
  const st = usePaymentStatus();
  if (!st) return null;
  return (
    <div className="mt-4 rounded-2xl bg-yellow-50 border-2 border-slate-900 p-4 text-sm" data-testid="payment-my-reference">
      <p className="font-semibold">Your payment reference</p>
      <p className="mt-1 text-base font-bold" data-testid="payment-reference-value">{st.reference || "Parent name_Child name_Grade"}</p>
    </div>
  );
}

function PaymentDetails({ loggedIn }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(ACCOUNT);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="nb-card bg-white p-8 mt-8" data-testid="payment-details">
      <h2 className="font-kids text-2xl font-bold flex items-center gap-2"><Landmark size={22} className="text-blue-600" /> Bank details</h2>
      <p className="mt-3 text-slate-700" data-testid="payment-instructions">
        Proceed with payment to the account below, then upload your screenshot above (or send it to{" "}
        <a href={`mailto:${PAY_EMAIL}`} className="font-semibold text-blue-600 underline" data-testid="payment-proof-email">{PAY_EMAIL}</a>).
      </p>
      <div className="mt-5 rounded-2xl bg-slate-50 border-2 border-slate-200 p-5 text-sm leading-6" data-testid="payment-account">
        <p className="font-bold text-base">KMP Lagoon Rentals Ltd</p>
        <p className="flex items-center gap-2 flex-wrap">
          MCB Account: <span className="font-mono font-bold text-base" data-testid="payment-account-number">{ACCOUNT}</span>
          <button onClick={copy} data-testid="payment-copy-account" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline">
            {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
          </button>
        </p>
        <p className="mt-2">C20 Oliviers Street Les Glaieuls,<br />Petit Verger, Mauritius</p>
        <p className="mt-2">Contact: <a href={`mailto:${PAY_EMAIL}`} className="text-blue-600 underline">{PAY_EMAIL}</a></p>
        <p>Tel: <a href="tel:+23058589125" className="text-blue-600 underline" data-testid="payment-phone">+230 58589125</a></p>
      </div>
      {loggedIn ? <MyReference /> : (
        <p className="mt-4 text-sm text-slate-600">Payment reference: Parent name_Child name_Grade</p>
      )}
    </motion.section>
  );
}

function Feat({ children }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      <Check size={18} className="text-green-600 shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  );
}

function Waitlist({ kids }) {
  const [w, setW] = useState(null);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/holiday/waitlist").then(({ data }) => { setW(data); setPick(Object.fromEntries(data.child_ids.map((i) => [i, true]))); }).catch(() => {}); }, []);
  const join = async () => {
    setBusy(true);
    try { const { data } = await api.post("/holiday/waitlist", { child_ids: Object.keys(pick).filter((k) => pick[k]) }); setW(data); toast.success("You're on the waiting list! / Vous êtes sur la liste d'attente !"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  if (!w || !kids.length) return null;
  return (
    <div className="mb-6 mt-4 rounded-2xl border-2 border-orange-300 bg-orange-50 p-4" data-testid="holiday-waitlist">
      <p className="font-bold flex items-center gap-2"><Palmtree size={16} className="text-orange-500" /> Holiday Pack fully booked · waiting list / liste d'attente</p>
      {w.joined && <p className="text-sm text-green-700 font-semibold mt-1" data-testid="holiday-waitlist-joined">You're on the waiting list — we'll contact you if a spot opens. / Vous êtes sur la liste d'attente, nous vous contacterons si une place se libère.</p>}
      <div className="flex flex-wrap gap-2 mt-3">
        {kids.map((c) => (
          <label key={c.id} className={`rounded-full border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${pick[c.id] ? "border-orange-500 bg-white" : "border-slate-200 bg-white/60"}`} data-testid={`holiday-waitlist-child-${c.id}`}>
            <input type="checkbox" className="mr-2" checked={Boolean(pick[c.id])} onChange={(e) => setPick({ ...pick, [c.id]: e.target.checked })} />{c.name} · Grade {c.grade}
          </label>
        ))}
      </div>
      <button onClick={join} disabled={busy || !Object.values(pick).some(Boolean)} data-testid="holiday-waitlist-join"
        className="mt-3 rounded-full bg-orange-500 text-white font-bold px-5 py-2 border-2 border-slate-900 disabled:opacity-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : w.joined ? "Update my waiting list" : "Join the waiting list / Rejoindre la liste d'attente"}
      </button>
    </div>
  );
}
